/**
 * Storage Layer
 * Phase 5: Data Layer - Storage Abstraction
 * Uses StorageRegistry and StorageAdapter for local-first persistence
 */

import { Block, Page } from '../types/block'
import { storage, initializeStorage } from '@core/storage-adapter'
import { generateId } from '@core/storage-adapter'
import { validationService, ValidationError, BackupError, StorageError } from './validation'

/**
 * Storage Keys
 */
const StorageKeys = {
  PAGE: (id: string) => `page:${id}`,
  BLOCKS: (pageId: string) => `blocks:${pageId}`,
  SCHEMA_VERSION: 'schemaVersion'
}

/**
 * Load data from storage
 */
async function load<T>(key: string): Promise<T | null> {
  try {
    const data = await storage.load(key)
    if (!data) return null

    // Convert date strings back to Date objects
    if (typeof data === 'object' && data !== null) {
      if (data.createdAt) data.createdAt = new Date(data.createdAt)
      if (data.updatedAt) data.updatedAt = new Date(data.updatedAt)
      if (data.lastSaved) data.lastSaved = new Date(data.lastSaved)
      if (data.lastAIProcessed) data.lastAIProcessed = new Date(data.lastAIProcessed)
    }

    return data as T
  } catch (error) {
    const errorMessage = `Failed to load ${key}: ${error instanceof Error ? error.message : String(error)}`
    console.error(errorMessage)
    
    // Create a detailed error object
    const storageError = new StorageError('load', errorMessage, key)
    
    // Log error with additional context
    console.error('Storage Error Details:', {
      key,
      error: error instanceof Error ? error.name : typeof error,
      timestamp: new Date().toISOString(),
      operation: 'load'
    })
    
    return null
  }
}

/**
 * Save data to storage
 */
async function save(key: string, data: any): Promise<void> {
  try {
    // Convert Date objects to strings for JSON serialization
    const dataWithDates = { ...data }
    if (dataWithDates.createdAt instanceof Date) {
      dataWithDates.createdAt = dataWithDates.createdAt.toISOString()
    }
    if (dataWithDates.updatedAt instanceof Date) {
      dataWithDates.updatedAt = dataWithDates.updatedAt.toISOString()
    }
    if (dataWithDates.lastSaved instanceof Date) {
      dataWithDates.lastSaved = dataWithDates.lastSaved.toISOString()
    }
    if (dataWithDates.lastAIProcessed instanceof Date) {
      dataWithDates.lastAIProcessed = dataWithDates.lastAIProcessed.toISOString()
    }

    await storage.save(key, dataWithDates)
  } catch (error) {
    const errorMessage = `Failed to save ${key}: ${error instanceof Error ? error.message : String(error)}`
    console.error(errorMessage)
    
    // Create a detailed error object
    const storageError = new StorageError('save', errorMessage, key)
    
    // Log error with additional context
    console.error('Storage Error Details:', {
      key,
      dataSize: JSON.stringify(data).length,
      error: error instanceof Error ? error.name : typeof error,
      timestamp: new Date().toISOString(),
      operation: 'save'
    })
    
    throw storageError
  }
}

/**
 * Save page with blocks
 */
export async function savePage(pageId: string, page: Page, blocks: Block[]): Promise<void> {
  // Validate the page before saving
  const validation = await validationService.validatePage(page)
  if (!validation.isValid) {
    throw new ValidationError('page', `Invalid page data: ${validation.errors.join(', ')}`)
  }

  // Validate the blocks before saving
  const blocksValidation = await validationService.validateBlocks(blocks)
  const hasInvalidBlocks = blocksValidation.some(result => !result.isValid)
  
  if (hasInvalidBlocks) {
    const allErrors = blocksValidation
      .filter(result => !result.isValid)
      .flatMap(result => result.errors)
      .join(', ')
    throw new ValidationError('blocks', `Invalid blocks data: ${allErrors}`)
  }

  const pageData = {
    ...page,
    blocks,
    updatedAt: new Date()
  }
  await save(StorageKeys.PAGE(pageId), pageData)
}

/**
 * Load page with blocks
 */
export async function loadPage(pageId: string): Promise<{ page: Page | null; blocks: Block[] }> {
  const pageData = await load<Page & { blocks: Block[] }>(StorageKeys.PAGE(pageId))
  if (!pageData) {
    return { page: null, blocks: [] }
  }

  return {
    page: {
      id: pageData.id,
      title: pageData.title,
      icon: pageData.icon,
      parentId: pageData.parentId || '',
      children: pageData.children || [],
      metadata: pageData.metadata,
      phase: pageData.phase,
      version: pageData.version
    },
    blocks: pageData.blocks || []
  }
}

/**
 * List all pages
 */
export async function listPages(): Promise<Page[]> {
  const keys = await storage.list('')
  const pages: Page[] = []

  for (const key of keys) {
    if (key.startsWith('page:')) {
      const pageId = key.substring(5) // Remove 'page:' prefix
      const pageData = await load<Page>(StorageKeys.PAGE(pageId))
      if (pageData) {
        pages.push(pageData)
      }
    }
  }

  return pages
}

/**
 * Create a new page
 */
export async function createPage(title: string, parentId?: string): Promise<Page> {
  const page: Page = {
    id: generateId(),
    title,
    icon: '📄',
    parentId: parentId || '',
    children: [],
    metadata: {
      createdAt: new Date(),
      updatedAt: new Date()
    },
    phase: 4,
    version: '1.0.0'
  }

  // Validate the page before saving
  const validation = await validationService.validatePage(page)
  if (!validation.isValid) {
    throw new ValidationError('page', `Invalid page data: ${validation.errors.join(', ')}`)
  }

  await save(StorageKeys.PAGE(page.id), page)
  return page
}

/**
 * Update page
 */
export async function updatePage(pageId: string, updates: Partial<Page>): Promise<void> {
  const existingPage = await load<Page>(StorageKeys.PAGE(pageId))
  if (!existingPage) {
    throw new Error(`Page ${pageId} not found`)
  }

  const updatedPage = {
    ...existingPage,
    ...updates,
    updatedAt: new Date()
  }

  // Validate the updated page before saving
  const validation = await validationService.validatePage(updatedPage)
  if (!validation.isValid) {
    throw new ValidationError('page', `Invalid page data: ${validation.errors.join(', ')}`)
  }

  await save(StorageKeys.PAGE(pageId), updatedPage)
}

/**
 * Delete page and all its blocks
 */
export async function deletePage(pageId: string): Promise<void> {
  await storage.delete(StorageKeys.PAGE(pageId))
}

/**
 * Save blocks for a page
 */
export async function saveBlocks(pageId: string, blocks: Block[]): Promise<void> {
  await save(StorageKeys.BLOCKS(pageId), blocks)
}

/**
 * Load blocks for a page
 */
export async function loadBlocks(pageId: string): Promise<Block[]> {
  const blocks = await load<Block[]>(StorageKeys.BLOCKS(pageId))
  return blocks || []
}

/**
 * Export all data as JSON
 */
export async function exportData(): Promise<Blob> {
  const data = await storage.export()
  
  // Validate the exported data structure
  try {
    const text = await data.text()
    const payload = JSON.parse(text)
    const validation = await validationService.validateStoragePayload(payload)
    
    if (!validation.isValid) {
      console.warn('Export data has validation issues:', validation.errors)
    } else {
      console.log('Export data validated successfully:', validation.validatedItems, 'items')
    }
  } catch (error) {
    console.warn('Could not validate exported data:', error)
  }
  
  return data
}

/**
 * Export pages only (without blocks)
 */
export async function exportPages(): Promise<Blob> {
  const pages = await listPages()
  const exportData = {
    version: '1.0.0',
    exportDate: new Date().toISOString(),
    schemaVersion: '5.0.0',
    pages,
    totalPages: pages.length,
    metadata: {
      exportedBy: 'FreshCards',
      format: 'pages-only'
    }
  }
  
  // Validate the export data
  const validation = await validationService.validateStoragePayload({
    pages,
    blocks: []
  })
  
  if (!validation.isValid) {
    console.warn('Export pages data has validation issues:', validation.errors)
  }
  
  return new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
}

/**
 * Export blocks only (grouped by page)
 */
export async function exportBlocks(): Promise<Blob> {
  const pages = await listPages()
  const blocksByPage: { [pageId: string]: Block[] } = {}
  
  for (const page of pages) {
    const blocks = await loadBlocks(page.id)
    blocksByPage[page.id] = blocks
  }
  
  const exportData = {
    version: '1.0.0',
    exportDate: new Date().toISOString(),
    schemaVersion: '5.0.0',
    blocksByPage,
    totalBlocks: Object.values(blocksByPage).reduce((sum, blocks) => sum + blocks.length, 0),
    metadata: {
      exportedBy: 'FreshCards',
      format: 'blocks-only'
    }
  }
  
  // Validate the blocks data
  const allBlocks = Object.values(blocksByPage).flat()
  const validation = await validationService.validateStoragePayload({
    pages: [],
    blocks: allBlocks
  })
  
  if (!validation.isValid) {
    console.warn('Export blocks data has validation issues:', validation.errors)
  }
  
  return new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
}

/**
 * Export data as markdown format
 */
export async function exportAsMarkdown(): Promise<Blob> {
  const pages = await listPages()
  let markdownContent = `# FreshCards Export\n\n`
  markdownContent += `Export Date: ${new Date().toLocaleString()}\n`
  markdownContent += `Total Pages: ${pages.length}\n\n`
  markdownContent += `---\n\n`
  
  for (const page of pages) {
    markdownContent += `## ${page.icon} ${page.title}\n\n`
    markdownContent += `**ID:** ${page.id}\n`
    markdownContent += `**Created:** ${page.metadata.createdAt.toLocaleString()}\n`
    markdownContent += `**Updated:** ${page.metadata.updatedAt.toLocaleString()}\n`
    
    if (page.parentId) {
      markdownContent += `**Parent:** ${page.parentId}\n`
    }
    
    markdownContent += `\n---\n\n`
    
    // Add page content
    const blocks = await loadBlocks(page.id)
    for (const block of blocks) {
      if (block.content) {
        markdownContent += `${block.content}\n\n`
      }
    }
    
    markdownContent += `\n---\n\n`
  }
  
  return new Blob([markdownContent], { type: 'text/markdown' })
}

/**
 * Export data as CSV format (pages summary)
 */
export async function exportAsCSV(): Promise<Blob> {
  const pages = await listPages()
  
  const csvHeaders = ['ID', 'Title', 'Icon', 'Parent ID', 'Children Count', 'Created', 'Updated', 'Phase']
  const csvRows = pages.map(page => [
    page.id,
    `"${page.title}"`,
    page.icon,
    page.parentId || '',
    (page.children || []).length,
    page.metadata.createdAt.toISOString(),
    page.metadata.updatedAt.toISOString(),
    page.phase
  ])
  
  const csvContent = [csvHeaders, ...csvRows]
    .map(row => row.map(cell => `"${cell}"`).join(','))
    .join('\n')
  
  return new Blob([csvContent], { type: 'text/csv' })
}

/**
 * Import data from blob
 */
export async function importData(data: Blob): Promise<void> {
  try {
    const text = await data.text()
    const payload = JSON.parse(text)
    
    // Validate the import payload before importing
    const validation = await validationService.validateImportPayload(payload)
    
    if (!validation.isValid) {
      throw new ValidationError('import', `Invalid import data: ${validation.errors.join(', ')}`)
    }
    
    if (validation.warnings.length > 0) {
      console.warn('Import data warnings:', validation.warnings)
    }
    
    console.log('Import data validated successfully:', validation.validatedItems, 'items')
    
    await storage.import(data)
  } catch (error) {
    if (error instanceof ValidationError) {
      throw error
    }
    throw new ValidationError('import', `Failed to import data: ${error instanceof Error ? error.message : String(error)}`)
  }
}

/**
 * Import pages from JSON file
 */
export async function importPagesFromJSON(jsonData: any): Promise<void> {
  try {
    // Validate the import payload
    const validation = await validationService.validateImportPayload({
      pages: jsonData.pages || [],
      blocks: []
    })
    
    if (!validation.isValid) {
      throw new ValidationError('import-pages', `Invalid pages data: ${validation.errors.join(', ')}`)
    }
    
    // Import each page
    for (const page of jsonData.pages) {
      await save(StorageKeys.PAGE(page.id), page)
    }
    
    console.log(`Successfully imported ${jsonData.pages.length} pages`)
  } catch (error) {
    if (error instanceof ValidationError) {
      throw error
    }
    throw new ValidationError('import-pages', `Failed to import pages: ${error instanceof Error ? error.message : String(error)}`)
  }
}

/**
 * Import blocks from JSON file
 */
export async function importBlocksFromJSON(jsonData: any): Promise<void> {
  try {
    const blocksByPage = jsonData.blocksByPage || {}
    let totalImported = 0
    
    // Import blocks for each page
    for (const [pageId, blocks] of Object.entries(blocksByPage)) {
      await save(StorageKeys.BLOCKS(pageId), blocks)
      totalImported += (blocks as Block[]).length
    }
    
    console.log(`Successfully imported ${totalImported} blocks across ${Object.keys(blocksByPage).length} pages`)
  } catch (error) {
    throw new ValidationError('import-blocks', `Failed to import blocks: ${error instanceof Error ? error.message : String(error)}`)
  }
}

/**
 * Import markdown content and convert to pages
 */
export async function importFromMarkdown(markdownText: string): Promise<void> {
  try {
    const pages: Page[] = []
    const blocks: { [pageId: string]: Block[] } = {}
    
    // Simple markdown parsing - split by ## headers
    const sections = markdownText.split(/^## /gm)
    
    // Skip the first empty section
    for (let i = 1; i < sections.length; i++) {
      const section = sections[i]
      const lines = section.split('\n')
      
      // First line is the title
      const titleLine = lines[0].trim()
      const title = titleLine.replace(/^.*?📄\s*/, '').replace(/^.*?\s*/, '') // Remove emoji and any remaining prefix
      
      // Create a new page
      const page: Page = {
        id: generateId(),
        title: title || `Imported Page ${i}`,
        icon: '📄',
        parentId: '',
        children: [],
        metadata: {
          createdAt: new Date(),
          updatedAt: new Date()
        },
        phase: 4,
        version: '1.0.0'
      }
      
      pages.push(page)
      
      // Process content lines (skip metadata and separators)
      const contentLines = lines.slice(1).filter(line =>
        !line.startsWith('**') &&
        !line.startsWith('---') &&
        line.trim() !== ''
      )
      
      // Create blocks for content
      const pageBlocks: Block[] = []
      let currentBlockContent = ''
      
      for (const line of contentLines) {
        if (line.trim() === '') {
          if (currentBlockContent.trim()) {
            pageBlocks.push({
              id: generateId(),
              type: 'text',
              content: currentBlockContent.trim(),
              metadata: {
                aiEligible: false
              },
              phase: 4
            })
            currentBlockContent = ''
          }
        } else {
          currentBlockContent += line + '\n'
        }
      }
      
      // Add the last block if it has content
      if (currentBlockContent.trim()) {
        pageBlocks.push({
          id: generateId(),
          type: 'text',
          content: currentBlockContent.trim(),
          metadata: {
            aiEligible: false
          },
          phase: 4
        })
      }
      
      blocks[page.id] = pageBlocks
    }
    
    // Validate and import all pages and blocks
    const validation = await validationService.validateImportPayload({
      pages,
      blocks: Object.values(blocks).flat()
    })
    
    if (!validation.isValid) {
      throw new ValidationError('import-markdown', `Invalid markdown data: ${validation.errors.join(', ')}`)
    }
    
    // Import all pages and blocks
    for (const page of pages) {
      await save(StorageKeys.PAGE(page.id), page)
      await save(StorageKeys.BLOCKS(page.id), blocks[page.id])
    }
    
    console.log(`Successfully imported ${pages.length} pages from markdown`)
  } catch (error) {
    if (error instanceof ValidationError) {
      throw error
    }
    throw new ValidationError('import-markdown', `Failed to import markdown: ${error instanceof Error ? error.message : String(error)}`)
  }
}

/**
 * Clear all data
 */
export async function clearAllData(): Promise<void> {
  try {
    const keysBefore = await storage.getKeys()
    await storage.clear()
    
    console.log('Successfully cleared all data', {
      keysCleared: keysBefore.length,
      timestamp: new Date().toISOString(),
      operation: 'clearAllData'
    })
  } catch (error) {
    const errorMessage = `Failed to clear all data: ${error instanceof Error ? error.message : String(error)}`
    console.error(errorMessage)
    
    const storageError = new StorageError('clear', errorMessage)
    console.error('Clear Data Error Details:', {
      error: error instanceof Error ? error.name : typeof error,
      timestamp: new Date().toISOString(),
      operation: 'clearAllData'
    })
    
    throw storageError
  }
}

/**
 * Initialize storage system
 */
export async function initializeStorageSystem(): Promise<void> {
  try {
    await initializeStorage()
    console.log('Storage system initialized successfully', {
      timestamp: new Date().toISOString(),
      operation: 'initializeStorageSystem'
    })
  } catch (error) {
    const errorMessage = `Failed to initialize storage system: ${error instanceof Error ? error.message : String(error)}`
    console.error(errorMessage)
    
    const storageError = new StorageError('initialize', errorMessage)
    console.error('Initialize Storage Error Details:', {
      error: error instanceof Error ? error.name : typeof error,
      timestamp: new Date().toISOString(),
      operation: 'initializeStorageSystem'
    })
    
    throw storageError
  }
}

/**
 * Get all storage keys
 */
export async function getStorageKeys(): Promise<string[]> {
  try {
    const keys = await storage.getKeys()
    console.log('Retrieved storage keys', {
      totalKeys: keys.length,
      timestamp: new Date().toISOString(),
      operation: 'getStorageKeys'
    })
    return keys
  } catch (error) {
    const errorMessage = `Failed to get storage keys: ${error instanceof Error ? error.message : String(error)}`
    console.error(errorMessage)
    
    const storageError = new StorageError('getKeys', errorMessage)
    console.error('Get Storage Keys Error Details:', {
      error: error instanceof Error ? error.name : typeof error,
      timestamp: new Date().toISOString(),
      operation: 'getStorageKeys'
    })
    
    throw storageError
  }
}

/**
 * Backup System
 */

/**
 * Create a backup of all data
 */
export async function createBackup(name?: string): Promise<{ backupId: string; backupDate: Date; size: number }> {
  try {
    const backupDate = new Date()
    const backupData = await storage.export()
    const backupId = `backup_${backupDate.getTime()}`
    
    // Create backup metadata
    const backupMetadata = {
      id: backupId,
      name: name || `Backup ${backupDate.toLocaleString()}`,
      date: backupDate.toISOString(),
      size: backupData.size,
      format: 'freshcards-v1',
      version: '1.0.0'
    }
    
    // Store backup metadata
    await save(`backup:${backupId}`, backupMetadata)
    
    // Store backup data
    await save(`backup:${backupId}:data`, backupData)
    
    console.log(`Backup created successfully: ${backupId}`)
    
    return {
      backupId,
      backupDate,
      size: backupData.size
    }
  } catch (error) {
    throw new BackupError('create', `Failed to create backup: ${error instanceof Error ? error.message : String(error)}`)
  }
}

/**
 * List all available backups
 */
export async function listBackups(): Promise<Array<{ id: string; name: string; date: Date; size: number }>> {
  try {
    const keys = await storage.list('')
    const backups: Array<{ id: string; name: string; date: Date; size: number }> = []
    
    for (const key of keys) {
      if (key.startsWith('backup:') && !key.endsWith(':data')) {
        const backupId = key.substring(7) // Remove 'backup:' prefix
        const backupData = await load(`backup:${backupId}`)
        
        if (backupData) {
          const backupMeta = backupData as any
          backups.push({
            id: backupId,
            name: backupMeta.name,
            date: new Date(backupMeta.date),
            size: backupMeta.size || 0
          })
        }
      }
    }
    
    // Sort by date (newest first)
    return backups.sort((a, b) => b.date.getTime() - a.date.getTime())
  } catch (error) {
    throw new BackupError('list', `Failed to list backups: ${error instanceof Error ? error.message : String(error)}`)
  }
}

/**
 * Restore from a backup
 */
export async function restoreFromBackup(backupId: string): Promise<void> {
  try {
    // Check if backup exists
    const backupData = await load(`backup:${backupId}`)
    if (!backupData) {
      throw new BackupError('restore', `Backup not found: ${backupId}`, backupId)
    }
    
    // Get the actual backup data
    const dataBlob = await load(`backup:${backupId}:data`) as Blob
    if (!dataBlob) {
      throw new BackupError('restore', `Backup data not found: ${backupId}`, backupId)
    }
    
    // Validate backup data before restoring
    try {
      const text = await (dataBlob as Blob).text()
      const payload = JSON.parse(text)
      const validation = await validationService.validateStoragePayload(payload)
      
      if (!validation.isValid) {
        throw new BackupError('restore', `Invalid backup data: ${validation.errors.join(', ')}`, backupId)
      }
      
      console.log(`Backup validation successful: ${validation.validatedItems} items`)
    } catch (error) {
      throw new BackupError('restore', `Backup validation failed: ${error instanceof Error ? error.message : String(error)}`, backupId)
    }
    
    // Create a backup of current data before restoring (safety measure)
    try {
      await createBackup('pre-restore-backup')
    } catch (error) {
      console.warn('Could not create pre-restore backup:', error)
    }
    
    // Clear current data
    await clearAllData()
    
    // Restore from backup
    await storage.import(dataBlob as Blob)
    
    console.log(`Successfully restored from backup: ${backupId}`)
    
  } catch (error) {
    if (error instanceof BackupError) {
      throw error
    }
    throw new BackupError('restore', `Failed to restore from backup: ${error instanceof Error ? error.message : String(error)}`, backupId)
  }
}

/**
 * Delete a backup
 */
export async function deleteBackup(backupId: string): Promise<void> {
  try {
    // Check if backup exists
    const backupData = await load(`backup:${backupId}`)
    if (!backupData) {
      throw new BackupError('delete', `Backup not found: ${backupId}`, backupId)
    }
    
    // Delete backup metadata and data
    await storage.delete(`backup:${backupId}`)
    await storage.delete(`backup:${backupId}:data`)
    
    console.log(`Backup deleted successfully: ${backupId}`)
    
  } catch (error) {
    if (error instanceof BackupError) {
      throw error
    }
    throw new BackupError('delete', `Failed to delete backup: ${error instanceof Error ? error.message : String(error)}`, backupId)
  }
}

/**
 * Export backup to file
 */
export async function exportBackupToFile(backupId: string): Promise<Blob> {
  try {
    const backupData = await load(`backup:${backupId}`)
    const dataBlob = await load(`backup:${backupId}:data`)
    
    if (!backupData || !dataBlob) {
      throw new BackupError('export', `Backup not found: ${backupId}`, backupId)
    }
    
    // Create a complete backup package
    const backupPackage = {
      metadata: backupData,
      data: await (dataBlob as Blob).text(),
      exportDate: new Date().toISOString()
    }
    
    return new Blob([JSON.stringify(backupPackage, null, 2)], { type: 'application/json' })
    
  } catch (error) {
    if (error instanceof BackupError) {
      throw error
    }
    throw new BackupError('export', `Failed to export backup: ${error instanceof Error ? error.message : String(error)}`, backupId)
  }
}

/**
 * Import backup from file
 */
export async function importBackupFromFile(file: File): Promise<void> {
  try {
    const text = await file.text()
    const backupPackage = JSON.parse(text)
    
    // Validate backup package structure
    if (!backupPackage.metadata || !backupPackage.data) {
      throw new BackupError('import', 'Invalid backup file format')
    }
    
    // Validate backup data
    const payload = JSON.parse(backupPackage.data)
    const validation = await validationService.validateStoragePayload(payload)
    
    if (!validation.isValid) {
      throw new BackupError('import', `Invalid backup data: ${validation.errors.join(', ')}`)
    }
    
    // Create new backup with imported data
    const backupId = `backup_${new Date().getTime()}`
    const backupMetadata = {
      ...backupPackage.metadata,
      id: backupId,
      date: new Date().toISOString(),
      importedFrom: backupPackage.metadata.id,
      importDate: new Date().toISOString()
    }
    
    // Store backup metadata and data
    await save(`backup:${backupId}`, backupMetadata)
    await save(`backup:${backupId}:data`, new Blob([backupPackage.data]))
    
    console.log(`Backup imported successfully: ${backupId}`)
    
  } catch (error) {
    if (error instanceof BackupError) {
      throw error
    }
    throw new BackupError('import', `Failed to import backup: ${error instanceof Error ? error.message : String(error)}`)
  }
}

/**
 * Auto backup system
 */

/**
 * Configure auto backup settings
 */
export interface AutoBackupConfig {
  enabled: boolean
  intervalMs: number // Backup interval in milliseconds
  maxBackups: number // Maximum number of backups to keep
  includeBlocks: boolean // Whether to include blocks in backups
}

/**
 * Get current auto backup configuration
 */
export async function getAutoBackupConfig(): Promise<AutoBackupConfig> {
  try {
    const config = await load<AutoBackupConfig>('auto-backup-config')
    return config || {
      enabled: false,
      intervalMs: 24 * 60 * 60 * 1000, // 24 hours
      maxBackups: 10,
      includeBlocks: true
    }
  } catch (error) {
    console.warn('Failed to get auto backup config:', error)
    return {
      enabled: false,
      intervalMs: 24 * 60 * 60 * 1000,
      maxBackups: 10,
      includeBlocks: true
    }
  }
}

/**
 * Set auto backup configuration
 */
export async function setAutoBackupConfig(config: AutoBackupConfig): Promise<void> {
  await save('auto-backup-config', config)
  console.log('Auto backup configuration updated:', config)
}

/**
 * Trigger manual auto backup
 */
export async function triggerAutoBackup(): Promise<void> {
  try {
    const config = await getAutoBackupConfig()
    if (!config.enabled) {
      console.log('Auto backup is disabled')
      return
    }
    
    await createBackup('Auto Backup')
    
    // Clean up old backups if we exceed maxBackups
    const backups = await listBackups()
    if (backups.length > config.maxBackups) {
      const backupsToDelete = backups.slice(config.maxBackups)
      for (const backup of backupsToDelete) {
        await deleteBackup(backup.id)
      }
      console.log(`Cleaned up ${backupsToDelete.length} old backups`)
    }
    
    console.log('Auto backup completed successfully')
    
  } catch (error) {
    console.error('Auto backup failed:', error)
    throw new BackupError('auto', `Auto backup failed: ${error instanceof Error ? error.message : String(error)}`)
  }
}

/**
 * Start auto backup scheduler
 */
export let autoBackupInterval: NodeJS.Timeout | null = null

export async function startAutoBackupScheduler(): Promise<void> {
  try {
    const config = await getAutoBackupConfig()
    if (!config.enabled) {
      console.log('Auto backup scheduler not started (disabled)')
      return
    }
    
    if (autoBackupInterval) {
      clearInterval(autoBackupInterval)
    }
    
    autoBackupInterval = setInterval(async () => {
      try {
        await triggerAutoBackup()
      } catch (error) {
        console.error('Scheduled auto backup failed:', error)
      }
    }, config.intervalMs)
    
    console.log(`Auto backup scheduler started with interval: ${config.intervalMs}ms`)
    
  } catch (error) {
    console.error('Failed to start auto backup scheduler:', error)
  }
}

/**
 * Stop auto backup scheduler
 */
export function stopAutoBackupScheduler(): void {
  if (autoBackupInterval) {
    clearInterval(autoBackupInterval)
    autoBackupInterval = null
    console.log('Auto backup scheduler stopped')
  }
}
